import React, { type FC, useEffect } from 'react';
import { connect, type MapStateToProps, useDispatch, useSelector } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import { PrematchSignature } from 'app/constants/matchGridStakes';
import { withinBettingContext } from 'app/contexts/betting';
import { Spinner } from 'app/design-system/components/Spinner';
import { asyncQueue } from 'app/lib/asyncQueue';
import useComponentWillMount from 'app/lib/hooks/useComponentWillMount';
import { selectSignalrConnected } from 'app/lib/signalr';
import MatchCenterDeferred from 'app/modules/MatchCenter';
import { useSSR } from 'app/packages/components/SkipSSR';
import NotFound from 'app/widgets/Errors/NotFound';

import {
  fetchFullMatchContent,
  PREMATCH_MATCHES_TOPIC,
  subscribeToPrematchMatches,
  unsubscribeFromPrematchMatches,
} from '../../actions';

import styles from './MatchCenter.module.scss';

type StoreProps = {
  matchId: number | null;
  matchNotFound: boolean;
  requested: boolean;
  loading: boolean;
};

const MatchCenterWithoutHOC: FC<StoreProps> = ({
  matchId,
  matchNotFound,
  requested,
  loading,
}) => {
  const isSSR = useSSR();
  const dispatch = useDispatch();
  const signalrConnected = useSelector(selectSignalrConnected);

  useComponentWillMount(() => {
    if (isSSR && !requested && matchId) {
      dispatch(fetchFullMatchContent({ matchId, signature: PrematchSignature.MATCH_SIGNATURE }));
    }
  });

  useEffect(() => {
    if (matchId && signalrConnected) {
      asyncQueue.addToQueue(
        `${PREMATCH_MATCHES_TOPIC}#${matchId}`,
        () => dispatch(subscribeToPrematchMatches(`${matchId}`, [matchId])),
      );
    }
  }, [matchId, signalrConnected]);

  useEffect(() => {
    return () => {
      asyncQueue.removeFromQueue(
        `${PREMATCH_MATCHES_TOPIC}#${matchId}`,
        () => dispatch(unsubscribeFromPrematchMatches(`${matchId}`)),
      );
    };
  }, []);

  if (!matchId) {
    return (
      <NotFound className={styles.notFoundContainer} />
    );
  }

  if (matchNotFound || (!requested && loading)) {
    return (
      <Spinner />
    );
  }

  if (matchNotFound && requested) {
    return (
      <NotFound className={styles.notFoundContainer} />
    );
  }

  return (
    <MatchCenterDeferred matchId={matchId} />
  );
};

const mapStateToProps: MapStateToProps<
  void,
  AnyType,
  ReduxState
> = (
  state: ReduxState,
  {
    $betting: {
      navigation: {
        selectors: {
          currentLocation,
        },
      },
      matchContent: {
        selectors: {
          matches,
          entityLoading,
          entityRequested,
        },
      },
    },
  },
): StoreProps => {
  const { matchId } = currentLocation(state);
  const match = matches(state).get(matchId);

  return {
    matchId,
    matchNotFound: !match,
    requested: entityRequested(state),
    loading: entityLoading(state),
  };
};

export const MatchCenter = compose<FC>(
  withinBettingContext,
  connect(mapStateToProps),
)(MatchCenterWithoutHOC);
